import { Box, Flex, Text } from '@everlywell/ui-kit';
import React from 'react';

import { EvaIcon } from '../../../components/PageSections/DynamicQuizModal/BiomarkerInsights/EvaIcon';
import { useAssistantMessageDisplay } from '../hooks/useAssistantMessageDisplay';
import type { BubbleProps } from '../types/components';

const WidgetBubble = ({
  shouldRenderHtml,
  sanitizedText,
  plainText,
}: {
  shouldRenderHtml: boolean;
  sanitizedText: string;
  plainText: string;
}) => (
  <Box
    alignSelf="flex-start"
    backgroundColor="rgba(0, 0, 0, 0.03)"
    borderRadius={12}
    padding="10px 12px"
    maxWidth="85%"
  >
    {shouldRenderHtml ? (
      // Security: sanitizedText is already sanitized by DOMPurify in useAssistantMessageText.
      <Box
        fontSize="md"
        color="tints.black"
        whiteSpace="pre-wrap"
        dangerouslySetInnerHTML={{ __html: sanitizedText }}
      />
    ) : (
      <Text fontSize="md" color="tints.black" whiteSpace="pre-wrap">
        {plainText}
      </Text>
    )}
  </Box>
);

const PageBubble = ({
  shouldRenderHtml,
  sanitizedText,
  plainText,
}: {
  shouldRenderHtml: boolean;
  sanitizedText: string;
  plainText: string;
}) =>
  shouldRenderHtml ? (
    // Security: sanitizedText is already sanitized by DOMPurify in useAssistantMessageText.
    <Box
      color="#0F0F0F"
      fontFamily="DM Sans"
      fontSize="1rem"
      fontWeight="400"
      lineHeight="150%"
      whiteSpace="pre-wrap"
      dangerouslySetInnerHTML={{ __html: sanitizedText }}
    />
  ) : (
    <Text
        color="tints.black"
        fontFamily="DM Sans"
        fontSize="1rem"
        fontWeight="400"
        lineHeight="150%"
        whiteSpace="pre-wrap"
      >
        {plainText}
      </Text>
  );

type AssistantAvatarProps = {
  isTyping?: boolean;
  isFadingOut?: boolean;
};

const AssistantAvatar = ({ isTyping = false, isFadingOut = false }: AssistantAvatarProps) => {
  // Determine which animation to use based on state
  const getAvatarSx = () => {
    if (isFadingOut) {
      // Quick fade out for transition to recommendations avatar
      return {
        animation: `avatarFadeOut 0.1s ease-out forwards`,
        '@keyframes avatarFadeOut': {
          '0%': { opacity: 1 },
          '100%': { opacity: 0 },
        },
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      };
    }
    if (isTyping) {
      return {
        animation: `pulse 2.5s ease-in-out infinite`,
        '@keyframes pulse': {
          '0%, 100%': {
            boxShadow: '0 0 0 0 rgba(230,173,255, 0)',
          },
          '50%': {
            boxShadow: '0 0 0 4px rgba(230,173,255, 0.4)',
          },
        },
      };
    }
    return undefined;
  };

  const getIconSx = () => {
    if (isFadingOut) return undefined;
    if (isTyping) {
      return {
        animation: 'scaleUp 2.5s ease-in-out infinite',
        '@keyframes scaleUp': {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.2)' },
        },
      };
    }
    return undefined;
  };

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      paddingTop={8}
      paddingRight={16}
      flexShrink={0}
    >
      <Box
        padding="4px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="56px"
        height="56px"
        minWidth="56px"
        minHeight="56px"
        flexShrink={0}
      >
        <Box
          backgroundColor="violet.lighter"
          borderRadius="full"
          padding={2}
          display="flex"
          alignItems="center"
          sx={getAvatarSx()}
        >
          <Box sx={getIconSx()}>
            <EvaIcon />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default function ChatAssistantMessage({
  text,
  variant = 'page',
  showLogo = false,
  isStreaming = false,
  shouldAnimate = false,
  onTypingComplete,
  shouldFadeOutAvatar = false,
}: BubbleProps): JSX.Element | null {
  const {
    shouldRender,
    displayPlainText,
    displaySanitizedText,
    displayHtml,
    isTypingComplete,
  } = useAssistantMessageDisplay({
    text,
    shouldAnimate,
    isStreaming,
    onTypingComplete,
  });

  if (!shouldRender) return null;

  if (variant === 'widget') {
    return (
      <WidgetBubble
        shouldRenderHtml={displayHtml}
        sanitizedText={displaySanitizedText}
        plainText={displayPlainText}
      />
    );
  }

  return (
    <Flex
      width="100%"
      justifyContent="flex-start"
      flexDirection="column"
      alignItems="flex-start"
    >
      <Box
        display="flex"
        maxWidth={600}
        width="fit-content"
        minHeight={44}
        flexDirection="column"
        justifyContent="center"
        borderRadius="22px 22px 22px 0"
        background="transparent"
      >
        <PageBubble
          shouldRenderHtml={displayHtml}
          sanitizedText={displaySanitizedText}
          plainText={displayPlainText}
        />
      </Box>

      {showLogo ? (
        <AssistantAvatar
          isTyping={isStreaming || (shouldAnimate && !isTypingComplete)}
          isFadingOut={shouldFadeOutAvatar && isTypingComplete}
        />
      ) : null}
    </Flex>
  );
}
